import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import Client from './components/Client';
import News from './components/News';
import NewsFilters from './components/NewsFilters';
import Visibility from './Visibility';

export default class NewsList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      news: null,
      addNew: false,
      filterData: undefined,
      limit: 20,
      hasMore: false,
      loadingMore: false,
      error: null,
    };

    this.applyFilters = this.applyFilters.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.newsUpdated = this.newsUpdated.bind(this);
    this.getData = this.getData.bind(this);

    this.restricted = new Visibility('news');
    this.filtersRef = React.createRef();
  }

  componentDidMount() {
    if (this.restricted.scope.visible) this.getData();
  }

  extractNews(data) {
    if (Array.isArray(data?.news)) return data.news;
    if (Array.isArray(data)) return data;
    return null;
  }

  loadNews(filters, limit, loadingMore = false) {
    const safeLimit = Math.min(Math.max(limit || 20, 20), 200);
    const queryData = {
      orderBy_modifiedOn: true,
      ...(filters || {}),
      limit: safeLimit,
    };

    this.setState({ error: null, loadingMore });
    try {
      const data = this.state.client.getData(
        this.props.API_URL_NEWS || '/api/1.0/news/',
        queryData,
      );
      const news = this.extractNews(data);
      if (!news) throw new Error('The server returned an invalid news list.');

      this.setState({
        news,
        filterData: filters,
        limit: safeLimit,
        hasMore: news.length === safeLimit && safeLimit < 200,
        loadingMore: false,
        error: null,
      });
    } catch (error) {
      console.error('NewsList.loadNews error:', error);
      this.setState((state) => ({
        news: state.news === null ? [] : state.news,
        loadingMore: false,
        error: 'News could not be loaded. Please retry.',
      }));
    }
  }

  /**
   * Base load: no filters, just latest news.
   * Backend handles pin_position + modifiedOn ordering.
   */
  getData(limit = this.state.limit) {
    this.loadNews(undefined, limit);
  }

  /**
   * Called by NewsFilters when user applies filters.
   * We enforce orderBy_modifiedOn by default, but allow filters
   * to add other params.
   */
  applyFilters(queryData) {
    const filters = {
      orderBy_modifiedOn: true,
      ...(queryData || {}),
    };
    delete filters.limit;
    this.loadNews(filters, 20);
  }

  toggleAdd() {
    if (!this.restricted.scope.edit) return;
    this.setState((s) => ({ addNew: !s.addNew }));
    window.scrollTo(0, 0);
  }

  newsUpdated(savedItem, previousIref) {
    const refreshCurrent = () => {
      this.loadNews(this.state.filterData, this.state.limit);
    };
    if (savedItem) {
      this.setState((state) => {
        const current = Array.isArray(state.news) ? state.news : [];
        const existingIndex = current.findIndex((item) => (
          String(item.iref) === String(previousIref)
          || String(item.iref) === String(savedItem.iref)
        ));
        const news = [...current];
        if (existingIndex >= 0) news[existingIndex] = savedItem;
        else news.unshift(savedItem);
        return { news, addNew: false, error: null };
      }, refreshCurrent);
    } else {
      this.setState({ addNew: false }, refreshCurrent);
    }

    // reset +/– button in NewsFilters
    if (this.filtersRef?.current) {
      this.filtersRef.current.setState({ addNew: false });
    }
  }

  loadMore = () => {
    if (this.state.loadingMore || !this.state.hasMore) return;
    this.loadNews(
      this.state.filterData,
      Math.min(this.state.limit + 20, 200),
      true,
    );
  };

  retry = () => {
    this.loadNews(this.state.filterData, this.state.limit);
  };

  render() {
    if (!this.restricted.scope.visible) return <div />;

    let list = null;
    let counter = 0;

    if (this.state.news) {
      counter = this.state.news.length;
      list = this.state.news.map((item) => (
        <News
          key={item.iref}
          item={item}
          onUpdated={this.newsUpdated}
          allowEdit={this.restricted.scope.edit}
          API_URL_NEWS={this.props.API_URL_NEWS}
          API_URL_LANGUAGE={this.props.API_URL_LANGUAGE}
          API_URL_COUNTY={this.props.API_URL_COUNTY}
        />
      ));
    }

    return (
      <div style={{ backgroundColor: '#273442', minHeight: '100%' }}>
        <div className="sticky">
          <NewsFilters
            ref={this.filtersRef}
            applyFilters={this.applyFilters}
            toggleAdd={this.toggleAdd}
            counter={counter}
            allowEdit={this.restricted.scope.edit}
          />
        </div>

        {this.state.addNew ? (
          <News
            key={0}
            item={undefined}
            isNew={true}
            onUpdated={this.newsUpdated}
            onCancel={this.toggleAdd}
            allowEdit={this.restricted.scope.edit}
            API_URL_NEWS={this.props.API_URL_NEWS}
            API_URL_LANGUAGE={this.props.API_URL_LANGUAGE}
            API_URL_COUNTY={this.props.API_URL_COUNTY}
          />
        ) : null}

        {this.state.news === null ? (
          <Box sx={{ p: 2 }}>
            <Skeleton animation="wave" variant="rounded" width="100%" height={180} />
            <Skeleton animation="wave" variant="rounded" width="100%" height={180} sx={{ mt: 1 }} />
          </Box>
        ) : this.state.news.length === 0 && !this.state.error ? (
          <Box sx={{ p: 3, color: '#e3e3e5', opacity: 0.85 }}>
            No news matches your filters. Try clearing or adjusting filters.
          </Box>
        ) : list}

        {this.state.error ? (
          <Box
            role="alert"
            sx={{
              m: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(211, 47, 47, 0.14)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2">{this.state.error}</Typography>
            <Button variant="outlined" color="inherit" onClick={this.retry}>
              Retry
            </Button>
          </Box>
        ) : null}

        {this.state.news && this.state.hasMore ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Button
              variant="outlined"
              onClick={this.loadMore}
              disabled={this.state.loadingMore}
              sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.5)' }}
              startIcon={this.state.loadingMore ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {this.state.loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        ) : null}
      </div>
    );
  }
}
